import React from 'react';
import { makeStyles } from '@material-ui/core';

import { useIntl } from 'ha/i18n';
import IconButton from 'ha/ui/next/IconButton';
import MenuList from 'ha/ui/next/MenuList';
import Divider from 'ha/ui/next/Divider';
import ListItemIcon from 'ha/ui/next/ListItemIcon';
import ListItemText from 'ha/ui/next/ListItemText';
import Collapse from 'ha/ui/next/Collapse';
import Button from 'ha/ui/next/Button';
import Grid from 'ha/ui/next/Grid';
import { Typography } from 'ha/ui/next/Typography';
import LanguageSwitcher from 'ha/modules/LanguageSwitcher';
import IntercomHandler from 'ha/components/IntercomHandler';

import ArrowUpIcon from 'streamline-icons/arrow-button-up-bold.svg';
import ArrowDownIcon from 'streamline-icons/arrow-button-down-bold.svg';
import LogoutIcon from 'streamline-icons/logout-1-bold.svg';
import HelpIcon from 'streamline-icons/question-circle-bold.svg';
import SupportIcon from 'streamline-icons/headphones-customer-support-human-bold.svg';

import { usePage } from 'ha/modules/Page/contexts/PageContext';
import { LOGIN, REGISTER } from 'ha/constants/pageNames';
import MenuItem from './MenuItem';
import { getLearnMenuLinks } from '../utils';
import AuthModalActions from '../containers/AuthModalActions';
import AuthenticatedMobileMenuItems from '../containers/AuthenticatedMobileMenuItems.lazy';
import useListYourPlaceCTA from '../hooks/useListYourPlaceCTA';

const useStyles = makeStyles(theme => ({
  languageSwitcher: {
    color: theme.palette.grey[700],
    width: '100%',
  },
  login: {
    margin: '1rem',
  },
  learnTitle: {
    margin: '1rem 1rem 0 1rem',
  },
  divider: {
    padding: '0 1rem',
    margin: '1rem 0',
  },
  landlordTitle: {
    margin: '0 1rem',
  },
  langWrapper: {
    padding: '1rem',
  },
}));

interface Props {
  isAuthenticated: boolean;
  logout: () => void;
}

export const MobileMainMenu: React.FC<Props> = ({
  isAuthenticated,
  logout,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const { T, urlResolver } = intl;
  const { name } = usePage();
  const isAuthPage = name === LOGIN || name === REGISTER;

  const [learnOpen, setLearnOpen] = React.useState(false);
  const toggleLearn = React.useCallback(() => {
    setLearnOpen(!learnOpen);
  }, [learnOpen]);

  const learnLinks = getLearnMenuLinks(intl);

  const ctaProps = useListYourPlaceCTA();

  return (
    <MenuList disablePadding data-test-locator="Mobile Main Menu">
      {isAuthenticated && <AuthenticatedMobileMenuItems />}
      {!isAuthenticated && !isAuthPage && (
        <AuthModalActions>
          {({ onRegisterClick, onLoginClick }) => (
            <div className={classes.login}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item>
                  <Button
                    data-test-locator="Register button"
                    onClick={onRegisterClick}
                    variant="contained"
                    color="primary"
                  >
                    {T('Sign up')}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    data-test-locator="Login button"
                    onClick={onLoginClick}
                    variant="outlined"
                    color="primary"
                  >
                    {T('Log in')}
                  </Button>
                </Grid>
              </Grid>
            </div>
          )}
        </AuthModalActions>
      )}
      <Divider />
      {ctaProps.isVisible && (
        <MenuItem to={ctaProps.href} rel={ctaProps.rel} divider>
          <ListItemText>
            <Typography.Paragraph textTransform="uppercase">
              {ctaProps.label}
            </Typography.Paragraph>
          </ListItemText>
        </MenuItem>
      )}
      <MenuItem to={urlResolver.getRenting()} divider>
        <ListItemText>
          <Typography.Paragraph textTransform="uppercase">
            {T('how_it_works_breadcrumb')}
          </Typography.Paragraph>
        </ListItemText>
      </MenuItem>
      <MenuItem to={urlResolver.getPricingTenantsUrl()} divider>
        <ListItemText>
          <Typography.Paragraph textTransform="uppercase">
            {T('pricing_link')}
          </Typography.Paragraph>
        </ListItemText>
      </MenuItem>
      <MenuItem onClick={toggleLearn}>
        <ListItemText>
          <Typography.Paragraph textTransform="uppercase">
            {T('navbar_learn_button')}
          </Typography.Paragraph>
        </ListItemText>
        <IconButton size="small">
          {learnOpen ? (
            <ArrowUpIcon fontSize="inherit" />
          ) : (
            <ArrowDownIcon fontSize="inherit" />
          )}
        </IconButton>
      </MenuItem>

      <Collapse in={learnOpen} timeout="auto">
        <MenuList disablePadding data-test-locator="Mobile Learn Menu">
          <div className={classes.learnTitle}>
            <Typography.Caption color="muted">
              {T('navbar_tenants_title')}
            </Typography.Caption>
          </div>
          {learnLinks.tenant.map(link => (
            <MenuItem
              key={link.title}
              to={link.url}
              rel={link.noFollow ? 'no-follow' : undefined}
            >
              <ListItemIcon iconSize="small">{link.icon}</ListItemIcon>
              <ListItemText>{link.title}</ListItemText>
            </MenuItem>
          ))}
          <div className={classes.divider}>
            <Divider />
          </div>
          <div className={classes.landlordTitle}>
            <Typography.Caption color="muted">
              {T('navbar_landlords_title')}
            </Typography.Caption>
          </div>
          {learnLinks.landlord.map(link => (
            <MenuItem
              key={link.title}
              to={link.url}
              rel={link.noFollow ? 'no-follow' : undefined}
            >
              <ListItemIcon iconSize="small">{link.icon}</ListItemIcon>
              <ListItemText>{link.title}</ListItemText>
            </MenuItem>
          ))}
        </MenuList>
      </Collapse>
      <Divider />
      <IntercomHandler data-test-locator="Intercom help" key="intercom-help">
        {({ show }) => (
          <MenuItem onClick={show}>
            <ListItemIcon iconSize="small">
              <HelpIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{T('Help')}</ListItemText>
          </MenuItem>
        )}
      </IntercomHandler>
      <MenuItem to={urlResolver.getContactUrl()}>
        <ListItemIcon iconSize="small">
          <SupportIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{T('Contact us')}</ListItemText>
      </MenuItem>
      {isAuthenticated && (
        <MenuItem onClick={logout}>
          <ListItemIcon iconSize="small">
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{T('Logout')}</ListItemText>
        </MenuItem>
      )}
      <Divider />
      <div className={classes.langWrapper}>
        <Typography.HelperText color="muted">
          {T('Select language')}
        </Typography.HelperText>
        <LanguageSwitcher className={classes.languageSwitcher} kind="full" />
      </div>
    </MenuList>
  );
};

export default MobileMainMenu;
